
import React, { useCallback, useRef, useEffect } from 'react';
import { useController } from 'react-hook-form';
import { v4 } from 'uuid';

/**
 * RadioButtonGroup Component
 * 
 * A wrapper component for GDS radio button groups that integrates with react-hook-form.
 * This component provides form validation, error handling, and accessibility features
 * while rendering a group of radio buttons using the GDS design system.
 * 
 * @component
 * @param {Object} props - The component props
 * @param {Object} props.control - React Hook Form control object for form state management
 * @param {string} props.name - The name attribute for the radio button group, used for form field identification
 * @param {Array<Object>} [props.options=[]] - Array of option objects for radio buttons
 * @param {string} props.options[].value - The value of the radio button option
 * @param {string} [props.options[].label] - The display label for the radio button (fallback to text if not provided)
 * @param {string} [props.options[].text] - Alternative display text for the radio button
 * @param {string} [props.options[].description] - Optional description text displayed below the radio button label
 * @param {React.ReactNode} [props.legend] - Legend text or element for the fieldset, typically the group title or question
 * @param {React.ReactNode} [props.descriptiveCopy] - Additional descriptive text displayed below the legend
 * @param {string} [props.layout="One Column"] - Layout configuration for the radio button group ("One Column" or "Two Column")
 * @param {React.ReactNode} [props.tooltip] - Tooltip content to display next to the legend for additional context
 * @param {string} [props.fieldsetAriaLabelledby] - ARIA labelledby attribute for the fieldset for accessibility
 * @param {boolean} [props.disabled] - Whether the entire radio button group should be disabled
 * @param {string} [props.defaultValue] - Default value for the radio button group when form value is not set
 * 
 * @returns {JSX.Element} The rendered radio button group component
 * 
 * @example
 * // Basic usage with Yes/No options
 * <RadioButtonGroup
 *   control={control}
 *   name="businessInFlorida"
 *   legend="Will you be conducting business physically in Florida?"
 *   options={[
 *     { value: "true", label: "Yes" },
 *     { value: "false", label: "No" }
 *   ]}
 * />
 * 
 * @example
 * // Two-column layout with tooltip and descriptions
 * <RadioButtonGroup
 *   control={control}
 *   name="paymentMethod"
 *   legend="Select your preferred payment method"
 *   tooltip="Choose the method you'll use most often"
 *   layout="Two Column"
 *   options={[
 *     { 
 *       value: "credit", 
 *       label: "Credit Card", 
 *       description: "Visa, MasterCard, or American Express" 
 *     },
 *     { 
 *       value: "bank", 
 *       label: "Bank Transfer", 
 *       description: "Direct transfer from your bank account" 
 *     }
 *   ]}
 * />
 */
export default function RadioButtonGroup({
    control,
    name,
    options = [],
    legend,
    descriptiveCopy,
    layout = "One Column",
    tooltip,
    fieldsetAriaLabelledby,
    disabled,
    defaultValue
}) {
    const gdsRadioButtonGroupRef = useRef();
    const groupIdRef = useRef(`radio-button-group-${v4()}`);

    const { field, fieldState: { error } } = useController({
        control,
        name,
        defaultValue
    });

    const handleValueChange = useCallback((e) => {
        field.onChange(e.detail?.value || "");
    }, [field.onChange]);

    useEffect(() => {
        gdsRadioButtonGroupRef.current?.addEventListener("value-change", handleValueChange);
        return () => {
            gdsRadioButtonGroupRef.current?.removeEventListener("value-change", handleValueChange);
        };
    }, [handleValueChange]);

    // Imperatively set active-index when field.value changes
    // This ensures the GDS web component updates even if React props don't trigger a re-render
    useEffect(() => {
        if (!gdsRadioButtonGroupRef.current) {
            return;
        }

        const isValueEmpty = field.value === undefined || field.value === null || field.value === '';

        if (isValueEmpty) {
            // Handle cleared value - reset the GDS web component state
            const clearRadioButtons = () => {
                if (gdsRadioButtonGroupRef.current) {
                    gdsRadioButtonGroupRef.current.setAttribute('active-index', '-1');
                    gdsRadioButtonGroupRef.current.activeIndex = -1;
                    const radioButtons = gdsRadioButtonGroupRef.current.querySelectorAll('gds-radio-button');
                    radioButtons.forEach((rb) => {
                        rb.removeAttribute('checked');
                        rb.checked = false;
                    });
                }
            };

            if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
                window.requestAnimationFrame(clearRadioButtons);
            } else {
                clearRadioButtons();
            }
        } else {
            const activeIndex = options.findIndex(o => o.value === field.value);
            if (activeIndex >= 0) {
                const updateRadioButtons = () => {
                    if (gdsRadioButtonGroupRef.current) {
                        // Try both attribute and property
                        gdsRadioButtonGroupRef.current.setAttribute('active-index', activeIndex.toString());
                        gdsRadioButtonGroupRef.current.activeIndex = activeIndex;
                        // Also try setting the 'checked' attribute on the individual radio button
                        const radioButtons = gdsRadioButtonGroupRef.current.querySelectorAll('gds-radio-button');
                        radioButtons.forEach((rb, index) => {
                            if (index === activeIndex) {
                                rb.setAttribute('checked', 'true');
                                rb.checked = true;
                            } else {
                                rb.removeAttribute('checked');
                                rb.checked = false;
                            }
                        });
                    }
                };

                // Use requestAnimationFrame to ensure DOM is ready, with fallback for SSR/test environments
                if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
                    window.requestAnimationFrame(updateRadioButtons);
                } else {
                    updateRadioButtons();
                }
            }
        }
    }, [field.value, options]);

    return (
        <gds-radio-button-group
            ref={gdsRadioButtonGroupRef}
            group-id={groupIdRef.current}
            layout={layout}
            fieldset-aria-labelledby={fieldsetAriaLabelledby}
            active-index={options.findIndex(o => o.value === field.value)}
            disabled={disabled || undefined}
        >
            {legend && (
                <span slot="legend">
                    {legend}
                    {tooltip && (
                        <gds-tooltip position="auto">
                            {tooltip}
                        </gds-tooltip>
                    )}
                </span>
            )}

            {descriptiveCopy && (
                <span slot="descriptive-copy">
                    {descriptiveCopy}
                </span>
            )}

            {options.map(option => (
                <gds-radio-button
                    key={option.value}
                    name={name}
                    value={option.value}
                >
                    <span slot="label">
                        {option.label || option.text}
                    </span>
                    {option.description && (
                        <p slot="description">{option.description}</p>
                    )}
                </gds-radio-button>
            ))}

            {error &&
                <span slot="error-message">{error.message}</span>
            }
        </gds-radio-button-group>
    );
}